
import { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Rx';
import * as epic from '../../epic';
import * as api from '../../apiClient';
import * as psa from './types'; 
import * as vt from '../venues/types';
import { IStore } from '../..';
import { parseLocalDateTime } from '../../../utils/util';
import { ProductStockLevel } from './types';

export type ProductStockActions = psa.LoadVenueStockLevels | psa.ReceiveVenueStockLevels | psa.LoadVenueStockOverview | psa.ReceiveVenueStockOverview
    | psa.ProductStockChanged | psa.ReceivedIndividualProductStock | psa.ReceivedIndividualProductStockOverview | psa.VenueChanged;

export const actionCreators = {
    loadVenueStockLevels: (stockDate: Date) => {
        return ({ type: psa.ProductStockActionTypes.RequestVenueStockLevels, stockDate: stockDate })
    },
    loadVenueStockLevelsComplete: (venueId: string, stockDate: Date, stockLevels: psa.ProductStockLevel[], err: api.ApiError | null) => {
        return ({ type: psa.ProductStockActionTypes.ReceivedVenueStockLevels, venueId: venueId, stockDate: stockDate, stockLevels: stockLevels, error: err })
    },
    loadVenueStockOverview: (stockDate: Date) => {
        return ({ type: psa.ProductStockActionTypes.RequestVenueStockOverview, stockDate: stockDate })
    },
    loadVenueStockOverviewComplete: (venueId: string, stockDate: Date, stockLevels: psa.ProductStockLevel[], err: api.ApiError | null) => {
        return ({ type: psa.ProductStockActionTypes.ReceivedVenueStockOverview, venueId: venueId, stockDate: stockDate, stockLevels: stockLevels, error: err })
    },
    productStockChanged: (venueId: string, productId: string, productVariantId: string | null) => {
        return ({ type: psa.ProductStockActionTypes.ProductStockChanged, venueId: venueId, productId: productId, productVariantId: productVariantId })
    },
    loadIndividualProductStockComplete: (stockLevels: psa.ProductStockLevel[], err: api.ApiError | null) => {
        return ({ type: psa.ProductStockActionTypes.ReceivedIndividualProductStock, stockLevels: stockLevels, error: err })
    },
    loadIndividualProductStockOverviewComplete: (stockLevels: psa.ProductStockLevel[], err: api.ApiError | null) => {
        return ({ type: psa.ProductStockActionTypes.ReceivedIndividualProductStockOverview, stockLevels: stockLevels, error: err })
    },
    venueChanged: (venueId: string) => ({ type: psa.ProductStockActionTypes.VenueChanged, venueId: venueId })
}

export interface GetStockLevelsResponse {
    stockLevels: ProductStockLevel[];
}

export const handleClientChange = (store: IStore) => [() => {
    return actionCreators.venueChanged(store.value.venues.selectedVenueId)
}]

//https://stackoverflow.com/questions/46481144/rxjs-how-to-retry-after-catching-and-processing-an-error-with-emitting-somethi

const loadVenueStockLevelsForDate = (venueId: string, stockDate: Date) => Observable.defer(() => {
        return api.getJson<GetStockLevelsResponse>(`api/v1/productStock/${venueId}/stockLevels/${stockDate.toApiDateOnlyString()}`)
    })
    .map(response => {
        return actionCreators.loadVenueStockLevelsComplete(venueId, stockDate, mapStockLevels(response.stockLevels), null)
    });

    const loadVenueStockOverviewForDate = (venueId: string, stockDate: Date) => Observable.defer(() => {
        return api.getJson<GetStockLevelsResponse>(`api/v1/productStock/${venueId}/stockLevelOverview/${stockDate.toApiDateOnlyString()}`)
    })
    .map(response => {
        return actionCreators.loadVenueStockOverviewComplete(venueId, stockDate, mapStockLevels(response.stockLevels), null)
    });

const loadSpecificProductStockForDate = (venueId: string, stockDate: Date, productId: string, productVariantId: string | null) =>
    Observable.defer(() => {
        return api.getJson<GetStockLevelsResponse>(`api/v1/productStock/${venueId}/productStockLevels/?startDate=${stockDate.toApiDateOnlyString()}&productId=${productId}&productVariantId=${productVariantId}`)
    })
    .map(response => {
        return actionCreators.loadIndividualProductStockComplete(mapStockLevels(response.stockLevels), null)
    });

const loadSpecificProductStockOverviewForDate = (venueId: string, stockDate: Date, productId: string, productVariantId: string | null) =>
    Observable.defer(() => {
        return api.getJson<GetStockLevelsResponse>(`api/v1/productStock/${venueId}/stockLevelOverview/${stockDate.toApiDateOnlyString()}/?productId=${productId}&productVariantId=${productVariantId}`)
    })
    .map(response => {
        return actionCreators.loadIndividualProductStockOverviewComplete(mapStockLevels(response.stockLevels), null)
    });

export const loadVenueStockLevelsEpic = (action$: ActionsObservable<any>, store: any) =>
    epic.create(action$,
        psa.ProductStockActionTypes.RequestVenueStockLevels,
        action => {
            return loadVenueStockLevelsForDate(store.value.venues.selectedVenueId, action.stockDate)
        },
        err => {
            return actionCreators.loadVenueStockLevelsComplete(store.value.venues.selectedVenueId, new Date(), [], err)
        });

export const loadVenueStockOverviewEpic = (action$: ActionsObservable<any>, store: any) =>
    epic.create(action$,
        psa.ProductStockActionTypes.RequestVenueStockOverview,
        action => {
            return loadVenueStockOverviewForDate(store.value.venues.selectedVenueId, action.stockDate)
        },
        err => {
            return actionCreators.loadVenueStockOverviewComplete(store.value.venues.selectedVenueId, new Date(), [], err)
        });

export const venueChangedEpic = (action$: ActionsObservable<any>, store: any) =>
    epic.create(action$,
        vt.VenueActionTypes.SelectVenue,
        action => {
            return loadVenueStockLevelsForDate(action.venueId, store.value.productStock.stockDate)
        },
        err => {
            return actionCreators.loadVenueStockLevelsComplete(store.value.venues.selectedVenueId, new Date(), [], err)
        },
        (action, _) => {
            return store.value.productStock.selectedVenueId === action.venueId;
        });

export const handleProductStockChangedEpic = (action$: ActionsObservable<any>, store: any) =>
    epic.create(action$,
        psa.ProductStockActionTypes.ProductStockChanged,
        action => Observable.merge(
            loadSpecificProductStockForDate(store.value.venues.selectedVenueId, store.value.productStock.stockDate, action.productId, action.productVariantId),
            loadSpecificProductStockOverviewForDate(store.value.venues.selectedVenueId, store.value.productStock.stockDate, action.productId, action.productVariantId)
        ),
        err => actionCreators.loadIndividualProductStockComplete([], err),
        (action, _) => {
            return store.value.venues.selectedVenueId === action.venueId;
        });

const mapStockLevels = (stockLevels: ProductStockLevel[]) => {
    return stockLevels.map(l => ({
        ...l,
        quantities: l.quantities.map(q =>
        ({
            ...q,
            dateInLocalTime: parseLocalDateTime(q.dateInLocalTime)
        }))
    }))
}